import { useState, useEffect } from "react";
import { C, API_BASE, getFontFamily } from "../constants/colors";
import { authedFetch } from "../firebase";

// ── LearnMode: Slide-based concept learning with quiz checkpoints ────────────
// Flow: Slide → Slide → Slide → Quiz → Slide → Slide → Slide → Quiz → Done
export function LearnMode({ concepts, chapter, subject, profile, lang, setLang, onBack, onPractice }) {
  const ff = getFontFamily(lang);

  // Group concepts into chunks of 3 for quiz checkpoints
  const chunkSize = 3;
  const chunks = [];
  for (let i = 0; i < concepts.length; i += chunkSize) {
    chunks.push(concepts.slice(i, i + chunkSize));
  }

  const [currentChunk, setCurrentChunk] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0); // within chunk
  const [phase, setPhase] = useState("slide"); // slide | quiz | result | complete
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizCurrent, setQuizCurrent] = useState(0);
  const [quizSelected, setQuizSelected] = useState(null);
  const [quizAnswered, setQuizAnswered] = useState(false);
  const [quizScores, setQuizScores] = useState([]);
  const [quizLoading, setQuizLoading] = useState(false);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);

  const chunk = chunks[currentChunk] || [];
  const concept = chunk[currentSlide];
  const totalSlides = concepts.length;
  const globalSlideIndex = currentChunk * chunkSize + currentSlide;
  const progress = ((globalSlideIndex + 1) / totalSlides) * 100;

  // Subject colors
  const sc = {
    Maths: { bg: C.amberSoft, accent: C.amberDark, iconBg: C.amberSoft },
    Science: { bg: C.primarySoft, accent: C.primaryMid, iconBg: C.primarySoft },
    Geography: { bg: C.greenSoft, accent: C.greenMid, iconBg: C.greenSoft },
    Economics: { bg: C.purpleSoft, accent: C.purpleMid, iconBg: C.purpleSoft },
  }[subject] || { bg: C.primarySoft, accent: C.primaryMid, iconBg: C.primarySoft };

  // Navigate slides
  const nextSlide = () => {
    if (currentSlide < chunk.length - 1) {
      setCurrentSlide(s => s + 1);
    } else {
      // End of chunk — trigger quiz
      if (currentChunk < chunks.length - 1) {
        loadQuiz();
      } else {
        setPhase("complete");
      }
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(s => s - 1);
    } else if (currentChunk > 0) {
      // Go back to previous chunk's last slide
      setCurrentChunk(c => c - 1);
      setCurrentSlide(chunks[currentChunk - 1].length - 1);
    }
  };

  // Register back handler for Android hardware back button
  // eslint-disable-next-line
  useEffect(() => {
    window.__samajhGoBack = () => {
      if (phase === "quiz" || phase === "result") {
        setPhase("slide");
        return true;
      }
      if (currentSlide > 0 || currentChunk > 0) {
        prevSlide();
        return true;
      }
      onBack?.();
      return true;
    };
    return () => { delete window.__samajhGoBack; };
  });

  // Generate quiz for current chunk
  const loadQuiz = async () => {
    setPhase("quiz");
    setQuizLoading(true);
    setQuizCurrent(0);
    setQuizSelected(null);
    setQuizAnswered(false);
    setQuizScores([]);

    const conceptNames = chunk.map(c => c.name).join(", ");
    const conceptDefs = chunk.map(c => `${c.name}: ${c.definition}`).join("\n");
    const formulas = chunk.flatMap(c => c.formulas || []).join(", ");

    try {
      const res = await authedFetch(`${API_BASE}/api/test-questions`, {
        method: "POST",
        body: JSON.stringify({
          topic: conceptNames,
          subject,
          studentClass: profile?.studentClass || 10,
          board: profile?.board || "CBSE",
          count: Math.min(chunk.length, 3),
          lang,
          conceptDefinition: conceptDefs,
          conceptFormulas: formulas ? [formulas] : [],
          chapterName: chapter,
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "[]";
      const cleaned = text.replace(/```json|```/g, "").trim();
      const qs = JSON.parse(cleaned);
      setQuizQuestions(qs);
    } catch (e) {
      console.error("Quiz load error:", e);
      // Skip quiz on error, move to next chunk
      setCurrentChunk(c => c + 1);
      setCurrentSlide(0);
      setPhase("slide");
    }
    setQuizLoading(false);
  };

  const handleQuizSelect = (idx) => {
    if (quizAnswered) return;
    setQuizSelected(idx);
    setQuizAnswered(true);
    const q = quizQuestions[quizCurrent];
    const correct = idx === q.correct;
    setQuizScores(s => [...s, { correct, explanation: q.explanation }]);
    if (correct) setTotalCorrect(c => c + 1);
    setTotalQuestions(t => t + 1);
  };

  const handleQuizNext = () => {
    if (quizCurrent < quizQuestions.length - 1) {
      setQuizCurrent(c => c + 1);
      setQuizSelected(null);
      setQuizAnswered(false);
    } else {
      // Quiz done — show results then move to next chunk
      setPhase("result");
    }
  };

  const continueAfterQuiz = () => {
    if (currentChunk < chunks.length - 1) {
      setCurrentChunk(c => c + 1);
      setCurrentSlide(0);
      setPhase("slide");
    } else {
      setPhase("complete");
    }
  };

  // ── Progress bar ──
  const ProgressBar = () => (
    <div style={{ padding: "12px 20px", background: C.surface, borderBottom: `0.5px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", fontSize: 13, color: C.textMid, cursor: "pointer", fontFamily: ff, fontWeight: 600 }}>
          ← {lang === "hi" ? "वापस" : "Back"}
        </button>
        <span style={{ fontSize: 12, color: C.textMute }}>{globalSlideIndex + 1} / {totalSlides}</span>
      </div>
      <div style={{ height: 4, background: C.borderLight, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: sc.accent, borderRadius: 2, transition: "width 0.3s ease" }} />
      </div>
    </div>
  );

  // ── Slide view ──
  if (phase === "slide" && concept) return (
    <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#E1F5EE", fontFamily: ff, display: "flex", flexDirection: "column" }}>
      <ProgressBar />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "20px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
        {/* Concept number badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: sc.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, color: sc.accent }}>
            {globalSlideIndex + 1}
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.textMute }}>{chapter}</div>
            <div style={{ fontSize: 17, fontWeight: 600, color: C.text, letterSpacing: "-0.02em" }}>{concept.name}</div>
          </div>
        </div>

        {/* Definition card */}
        <div style={{ background: C.surface, borderRadius: 16, padding: "22px", marginBottom: 12, border: `0.5px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: sc.accent, letterSpacing: "0.03em", marginBottom: 10 }}>
            {lang === "hi" ? "परिभाषा" : "What is it?"}
          </div>
          <div style={{ fontSize: 15, color: C.text, lineHeight: 1.7 }}>
            {concept.definition}
          </div>
        </div>

        {/* Formulas card (if any) */}
        {concept.formulas && concept.formulas.length > 0 && (
          <div style={{ background: sc.bg, borderRadius: 16, padding: "18px 22px", marginBottom: 12, border: `1px solid ${sc.accent}22` }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: sc.accent, letterSpacing: "0.03em", marginBottom: 10 }}>
              {lang === "hi" ? "सूत्र" : "Key formulas"}
            </div>
            {concept.formulas.map((f, i) => (
              <div key={i} style={{ fontFamily: "'Outfit', monospace", fontSize: 15, color: sc.accent, fontWeight: 600, padding: "6px 0", lineHeight: 1.6 }}>
                {f}
              </div>
            ))}
          </div>
        )}

        {/* Prerequisites hint */}
        {concept.prerequisites && concept.prerequisites.length > 0 && (
          <div style={{ fontSize: 12, color: C.textMute, padding: "8px 0", lineHeight: 1.5 }}>
            {lang === "hi" ? "आवश्यक अवधारणाएं: " : "Builds on: "}
            {concept.prerequisites.join(", ")}
          </div>
        )}

        {/* Quiz checkpoint indicator */}
        {currentSlide === chunk.length - 1 && currentChunk < chunks.length - 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, padding: "10px 14px", background: C.amberSoft, borderRadius: 10, border: `1px solid ${C.amberBorder}` }}>
            <span style={{ fontSize: 14 }}>📝</span>
            <span style={{ fontSize: 13, color: C.amberDark }}>
              {lang === "hi" ? "अगला: छोटा क्विज़ (2-3 सवाल)" : "Next up: Quick quiz (2-3 questions)"}
            </span>
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Navigation */}
        <div style={{ display: "flex", gap: 10, paddingTop: 16 }}>
          {(currentSlide > 0 || currentChunk > 0) && (
            <button onClick={prevSlide} style={{
              padding: "13px 20px", background: C.surface, border: `0.5px solid ${C.border}`,
              borderRadius: 12, fontSize: 14, fontWeight: 600, color: C.textMid,
              cursor: "pointer", fontFamily: ff,
            }}>
              ← {lang === "hi" ? "पिछला" : "Previous"}
            </button>
          )}
          <button onClick={nextSlide} style={{
            flex: 1, padding: "13px", background: sc.accent, color: "white",
            border: "none", borderRadius: 12, fontSize: 14, fontWeight: 600,
            cursor: "pointer", fontFamily: ff,
          }}>
            {currentSlide === chunk.length - 1
              ? (currentChunk < chunks.length - 1
                ? (lang === "hi" ? "क्विज़ शुरू करें →" : "Start quiz →")
                : (lang === "hi" ? "पूरा करें →" : "Complete →"))
              : (lang === "hi" ? "अगला →" : "Next →")}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Quiz loading ──
  if (phase === "quiz" && quizLoading) return (
    <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#E1F5EE", fontFamily: ff, display: "flex", flexDirection: "column" }}>
      <ProgressBar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map(i => <div key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: sc.accent, animation: `td 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}
        </div>
        <div style={{ fontSize: 14, color: C.textMute }}>{lang === "hi" ? "क्विज़ तैयार हो रहा है..." : "Preparing your quiz..."}</div>
      </div>
    </div>
  );

  // ── Quiz question ──
  if (phase === "quiz" && quizQuestions.length > 0) {
    const q = quizQuestions[quizCurrent];
    return (
      <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#E1F5EE", fontFamily: ff, display: "flex", flexDirection: "column" }}>
        <ProgressBar />

        <div style={{ flex: 1, padding: "20px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
          {/* Quiz header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: C.amberSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: 14 }}>📝</span>
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.textMute }}>{lang === "hi" ? "चेकपॉइंट क्विज़" : "Checkpoint quiz"}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{quizCurrent + 1} / {quizQuestions.length}</div>
              </div>
            </div>
          </div>

          {/* Question */}
          <div style={{ background: C.surface, borderRadius: 16, padding: "22px", marginBottom: 16, border: `0.5px solid ${C.border}` }}>
            <div style={{ fontSize: 15, color: C.text, lineHeight: 1.7, fontWeight: 500 }}>{q.question}</div>
          </div>

          {/* Options */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            {q.options.map((opt, idx) => {
              let bg = C.surface;
              let borderColor = C.border;
              let textColor = C.text;

              if (quizAnswered) {
                if (idx === q.correct) {
                  bg = "#EAF3DE";
                  borderColor = C.greenMid;
                  textColor = "#27500A";
                } else if (idx === quizSelected && !quizScores[quizScores.length - 1]?.correct) {
                  bg = "#FCEBEB";
                  borderColor = "#E24B4A";
                  textColor = "#791F1F";
                }
              }

              return (
                <button key={idx} onClick={() => handleQuizSelect(idx)} disabled={quizAnswered} style={{
                  padding: "14px 16px", background: bg, border: `1px solid ${borderColor}`,
                  borderRadius: 12, fontSize: 14, color: textColor, cursor: quizAnswered ? "default" : "pointer",
                  fontFamily: ff, fontWeight: 500, textAlign: "left", transition: "all .12s",
                  display: "flex", alignItems: "center", gap: 12,
                }}
                  onMouseOver={e => { if (!quizAnswered) { e.currentTarget.style.borderColor = sc.accent; e.currentTarget.style.background = sc.bg; } }}
                  onMouseOut={e => { if (!quizAnswered) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; } }}
                >
                  <span style={{ width: 24, height: 24, borderRadius: 8, background: quizAnswered && idx === q.correct ? C.greenMid : (quizAnswered && idx === quizSelected ? "#E24B4A" : C.borderLight), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: quizAnswered && (idx === q.correct || idx === quizSelected) ? "white" : C.textMid, flexShrink: 0 }}>
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span style={{ flex: 1 }}>{opt}</span>
                  {quizAnswered && idx === q.correct && <span style={{ fontSize: 14 }}>✓</span>}
                </button>
              );
            })}
          </div>

          {/* Explanation after answering */}
          {quizAnswered && q.explanation && (
            <div style={{ background: quizScores[quizScores.length - 1]?.correct ? "#EAF3DE" : "#FCEBEB", borderRadius: 12, padding: "14px 16px", marginBottom: 16, fontSize: 13, color: C.text, lineHeight: 1.6 }}>
              <span style={{ fontWeight: 600 }}>{quizScores[quizScores.length - 1]?.correct ? (lang === "hi" ? "सही! " : "Correct! ") : (lang === "hi" ? "गलत। " : "Not quite. ")}</span>
              {q.explanation}
            </div>
          )}

          {/* Next button */}
          {quizAnswered && (
            <button onClick={handleQuizNext} style={{
              width: "100%", padding: "14px", background: sc.accent, color: "white",
              border: "none", borderRadius: 12, fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: ff,
            }}>
              {quizCurrent < quizQuestions.length - 1
                ? (lang === "hi" ? "अगला सवाल →" : "Next question →")
                : (lang === "hi" ? "परिणाम देखें →" : "See results →")}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Quiz results ──
  if (phase === "result") {
    const correct = quizScores.filter(s => s.correct).length;
    const total = quizScores.length;
    const allCorrect = correct === total;

    return (
      <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#E1F5EE", fontFamily: ff, display: "flex", flexDirection: "column" }}>
        <ProgressBar />

        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px", maxWidth: 400, margin: "0 auto" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{allCorrect ? "🎯" : correct > 0 ? "💪" : "📖"}</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: C.text, marginBottom: 8, textAlign: "center" }}>
            {allCorrect
              ? (lang === "hi" ? "शानदार! सब सही!" : "Perfect! All correct!")
              : (lang === "hi" ? `${correct}/${total} सही` : `${correct}/${total} correct`)}
          </div>
          <div style={{ fontSize: 13, color: C.textMute, marginBottom: 28, textAlign: "center", lineHeight: 1.6 }}>
            {allCorrect
              ? (lang === "hi" ? "आगे बढ़ने के लिए तैयार हैं।" : "You're ready to continue.")
              : (lang === "hi" ? "कोई बात नहीं — आगे बढ़ते हैं।" : "No worries — let's keep going.")}
          </div>
          <button onClick={continueAfterQuiz} style={{
            width: "100%", padding: "14px", background: sc.accent, color: "white",
            border: "none", borderRadius: 12, fontSize: 14, fontWeight: 600,
            cursor: "pointer", fontFamily: ff, marginBottom: 10,
          }}>
            {currentChunk < chunks.length - 1
              ? (lang === "hi" ? "अगले अवधारणाएं →" : "Continue learning →")
              : (lang === "hi" ? "अध्याय पूरा करें →" : "Complete chapter →")}
          </button>
        </div>
      </div>
    );
  }

  // ── Complete ──
  if (phase === "complete") return (
    <div style={{ minHeight: "calc(100vh / 0.92)", zoom: 0.92, background: "#E1F5EE", fontFamily: ff, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div style={{ maxWidth: 400, textAlign: "center" }}>
        <div style={{ fontSize: 56, marginBottom: 20 }}>🏆</div>
        <div style={{ fontSize: 22, fontWeight: 600, color: C.text, marginBottom: 8 }}>
          {lang === "hi" ? "अध्याय पूरा!" : "Chapter complete!"}
        </div>
        <div style={{ fontSize: 15, color: C.textMid, marginBottom: 8 }}>{chapter}</div>
        <div style={{ fontSize: 13, color: C.textMute, marginBottom: 28, lineHeight: 1.6 }}>
          {lang === "hi"
            ? `${concepts.length} अवधारणाएं सीखीं${totalQuestions > 0 ? ` · क्विज़: ${totalCorrect}/${totalQuestions} सही` : ""}`
            : `${concepts.length} concepts covered${totalQuestions > 0 ? ` · Quiz: ${totalCorrect}/${totalQuestions} correct` : ""}`}
        </div>

        <button onClick={() => onPractice && onPractice(concepts[0]?.name, subject)} style={{
          width: "100%", padding: "14px", background: sc.accent, color: "white",
          border: "none", borderRadius: 12, fontSize: 15, fontWeight: 600,
          cursor: "pointer", fontFamily: ff, marginBottom: 10,
        }}>
          {lang === "hi" ? "इस अध्याय के सवाल हल करें →" : "Practice this chapter →"}
        </button>
        <button onClick={onBack} style={{
          width: "100%", padding: "12px", background: C.surface, color: C.textMid,
          border: `0.5px solid ${C.border}`, borderRadius: 12, fontSize: 14, fontWeight: 600,
          cursor: "pointer", fontFamily: ff,
        }}>
          {lang === "hi" ? "← अध्याय चुनें" : "← Choose another chapter"}
        </button>
      </div>
    </div>
  );

  return null;
}
